/**
 * Cliente de la ruleta: pinta lo que manda el servidor, anuncia lo que pasa y
 * envía las intenciones del jugador.
 *
 * El cliente no decide nada del juego. Su trabajo es que **todo lo jugable se
 * pueda oír**: convierte cada evento en una frase (`narration.ts`), la manda a
 * la región de anuncios y deja a mano una consulta para volver a pedir cualquier
 * cosa que se haya escapado.
 *
 * Dos costumbres que se repiten por todo el archivo y conviene entender antes de
 * tocarlo:
 *
 * - **Botones, nunca teclas sueltas.** El lector de pantalla se queda las letras
 *   en modo exploración, así que las acciones son botones y los atajos llevan
 *   Alt, que sí llega a la página.
 * - **Un botón desactivado no explica nada** (no recibe el foco y su `title` no
 *   se lee). Cuando algo no se puede hacer, en vez del botón apagado se escribe
 *   el motivo a la vista.
 */

import {
  BOT_DIFFICULTIES,
  type BotDifficulty,
  type GameConfig,
  type GameEvent,
  type GameView,
  type PanelLength,
  type PanelVerbosity,
  type PlayerView,
  type RoundView,
  type ServerMessage,
} from '../shared/protocol.js';
import { CONSONANTS, type Letter, VOWELS } from '../shared/letters.js';
import {
  describeCell,
  describeEvent,
  describeMoney,
  describePanel,
  describeTimeLeft,
  describeTurn,
  describeUsedLetters,
  describeWord,
  describeWordHeader,
  money,
} from './narration.js';
import { MessageHistory, historyIndexFromKey, plain } from './history.js';
import { SoundEngine } from './audio.js';
import { HelpScreen } from './help.js';
import { Net } from './net.js';
import { ThemeSwitcher } from './theme.js';

/** Clave en localStorage de la forma de leer el panel (es de cada jugador). */
const VERBOSITY_KEY = 'ruleta-verbosity';

// --- Elementos --------------------------------------------------------------

/** Busca un elemento por id y se queja si no está: un id mal escrito se ve al instante. */
function byId<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Falta el elemento #${id} en el HTML`);
  return element as T;
}

const announceRegion = byId('announce');
const historyRegion = byId('history');
const joinScreen = byId('join-screen');
const gameScreen = byId('game-screen');
const helpScreenElement = byId('help-screen');
const joinForm = byId<HTMLFormElement>('join-form');
const roomInput = byId<HTMLInputElement>('room-input');
const nameInput = byId<HTMLInputElement>('name-input');
const joinError = byId('join-error');
const roomLabel = byId('room-label');
const statusLine = byId('status');
const roundTools = byId('round-tools');
const configSection = byId('config-section');
const panelSection = byId('panel-section');
const panelCategory = byId('panel-category');
const panelVisual = byId('panel-visual');
const panelWords = byId<HTMLUListElement>('panel-words');
const playersList = byId<HTMLUListElement>('players');
const actionsBox = byId('actions');
const skipLink = byId('skip-link');
const verbosityButton = byId<HTMLButtonElement>('btn-verbosity');
const muteButton = byId<HTMLButtonElement>('btn-mute');
const timeButton = byId<HTMLButtonElement>('btn-time');

// --- Estado -----------------------------------------------------------------

let state: GameView | null = null;
let meId: string | null = null;
/**
 * Paso intermedio que solo existe en el cliente: elegir vocal o escribir la
 * solución son dos pantallas de mandos, no dos estados del juego.
 */
let pending: 'none' | 'vowel' | 'solve' = 'none';
/** Marca del último turno anunciado, para no repetir el aviso en cada refresco. */
let announcedTurn = '';

let verbosity: PanelVerbosity = localStorage.getItem(VERBOSITY_KEY) === 'cell' ? 'cell' : 'grouped';

/**
 * Forma que tenía la lista del panel la última vez que se montó.
 *
 * Sirve para decidir si el repintado puede escribir encima de los nodos que ya
 * hay o hace falta rehacerlos. Ver `renderPanel`.
 */
let panelShape: string | null = null;

const history = new MessageHistory();
const sound = new SoundEngine();
const theme = new ThemeSwitcher();
const help = new HelpScreen({
  help: helpScreenElement,
  others: [joinScreen, gameScreen],
  // Con el manual delante, el destino del atajo queda oculto y el enlace no
  // llevaría a ninguna parte.
  onToggle: (open) => {
    skipLink.hidden = open || gameScreen.hidden;
  },
});

// --- Anuncios ---------------------------------------------------------------

/**
 * Avisos pendientes de leer.
 *
 * Una sola acción del servidor llega como **varios eventos seguidos** (tira,
 * sale quiebra, pierde el turno, empieza otro) y cada uno en su mensaje de red,
 * así que no caen todos en el mismo tic del navegador. Si cada uno escribiera en
 * la región, el lector de pantalla se quedaría solo con el último y el jugador
 * no llegaría a oír en qué casilla ha caído. Por eso se espera un poco a que
 * lleguen los que vengan detrás y se leen juntos.
 */
let pendingAnnouncements: string[] = [];
let flushHandle: number | null = null;
/** Instante del primer aviso del grupo, para no esperar indefinidamente. */
let groupStartedAt = 0;

/** Margen para que lleguen los eventos de la misma jugada, sin que se note. */
const ANNOUNCE_DEBOUNCE_MS = 150;
/** Tope de espera: una cadena larga de eventos no puede retrasar el aviso más. */
const ANNOUNCE_MAX_WAIT_MS = 700;

/**
 * @brief Encola un aviso para leerlo junto a los de la misma jugada.
 * @param text Frase a anunciar.
 */
function announce(text: string): void {
  if (!text) return;
  const now = Date.now();
  if (pendingAnnouncements.length === 0) groupStartedAt = now;
  pendingAnnouncements.push(text);
  if (flushHandle !== null) window.clearTimeout(flushHandle);
  const wait = Math.max(0, Math.min(ANNOUNCE_DEBOUNCE_MS, groupStartedAt + ANNOUNCE_MAX_WAIT_MS - now));
  flushHandle = window.setTimeout(flushAnnouncements, wait);
}

/** Vuelca los avisos acumulados a la región y los guarda para repetirlos. */
function flushAnnouncements(): void {
  flushHandle = null;
  if (pendingAnnouncements.length === 0) return;
  const joined = pendingAnnouncements.join(' ');
  pendingAnnouncements = [];
  announceRegion.textContent = joined;
  history.record(joined);
}

/**
 * @brief Responde a una consulta del jugador.
 *
 * Va por la otra región: lo que se pide a mano no debe pisar lo que está
 * pasando en la partida, ni al revés.
 *
 * @param text Respuesta a leer.
 */
function reply(text: string): void {
  historyRegion.textContent = text;
  history.record(text);
}

// --- Conexión ---------------------------------------------------------------

const net = new Net({
  onOpen: () => {
    joinError.textContent = '';
  },
  onMessage: handleMessage,
  onClose: () => {
    statusLine.textContent = 'Se ha perdido la conexión. Reintentando…';
  },
});

/**
 * @brief Reparte cada mensaje del servidor.
 * @param message Mensaje recibido.
 */
function handleMessage(message: ServerMessage): void {
  switch (message.type) {
    case 'joined':
      meId = message.playerId;
      joinScreen.hidden = true;
      gameScreen.hidden = false;
      skipLink.hidden = false;
      break;
    case 'state':
      state = message.state;
      render();
      break;
    case 'event':
      handleEvent(message.event);
      break;
    case 'error':
      if (gameScreen.hidden) joinError.textContent = message.message;
      else announce(message.message);
      break;
  }
}

/** Convierte un evento en aviso y apunta si hay que releer el panel después. */
function handleEvent(event: GameEvent): void {
  const text = describeEvent(event, state?.players ?? [], meId, state?.config.wheelStop === 'manual');
  if (text) announce(text);
  playEventSound(event);
  // Tras descubrirse letras, el panel entero: primero lo nuevo (ya va en el
  // evento) y después cómo queda, que es el orden en que se sigue de oído.
  const revealed =
    ((event.kind === 'letterAsked' || event.kind === 'vowelTaken') && event.count > 0) ||
    event.kind === 'finalRevealed' ||
    event.kind === 'finalStarted';
  if (revealed) panelNeedsReading = true;
}

/**
 * Pone el sonido que le toca a cada evento.
 *
 * El sonido acompaña al aviso hablado, nunca lo sustituye: si aquí no suena
 * nada, el jugador se entera igual de todo por la región de anuncios.
 */
function playEventSound(event: GameEvent): void {
  switch (event.kind) {
    case 'wheelStarted':
      // Suena hasta que se pare sola; si la paran antes, el sonido de la casilla
      // llega igual porque el tictac no bloquea nada.
      sound.spin((event.endsAt - Date.now()) / 1000);
      break;
    case 'spun':
      // En el modo «a mano» la rueda ya venía sonando: se corta ahí mismo, o
      // seguiría oyéndose girar después de haberse parado.
      if (state?.config.wheelStop === 'manual') sound.stopSpin();
      else sound.spin();
      break;
    case 'letterAsked':
      sound.play(event.count > 0 ? 'letterHit' : 'letterMiss');
      break;
    case 'vowelTaken':
      sound.play(event.count > 0 ? 'vowel' : 'letterMiss');
      break;
    case 'bankrupt':
      sound.play('bankrupt');
      // La quiebra ya suena bastante fea; el «cambia el turno» que viene detrás
      // no necesita su propio sonido encima.
      bankruptJustPlayed = true;
      break;
    case 'turnLost':
      if (event.reason === 'wedge' && !bankruptJustPlayed) sound.play('loseTurn');
      bankruptJustPlayed = false;
      break;
    case 'wildcardGained':
    case 'surpriseGained':
    case 'doublerGained':
    case 'jackpotEntered':
    case 'expressStarted':
      sound.play('prize');
      break;
    case 'solved':
      sound.play('solved');
      break;
    case 'roundStarted':
    case 'finalStarted':
      sound.play('roundStart');
      break;
    case 'finalLetterPicked':
      sound.play('turn');
      break;
    case 'finalRevealed':
      sound.play(event.found.length > 0 ? 'letterHit' : 'letterMiss');
      break;
    case 'finalTimeUp':
      sound.play('bankrupt');
      break;
    case 'turnStarted':
      // Solo el turno propio: un pitido por cada turno de bot sería ruido.
      if (event.playerId === meId) sound.play('turn');
      break;
    case 'playerJoined':
      sound.play('join');
      break;
    case 'gameOver':
      sound.play('gameOver');
      break;
    default:
      break;
  }
}

/** Marca para no encadenar el sonido de quiebra con el de perder turno. */
let bankruptJustPlayed = false;

/**
 * true si hay que releer el panel en cuanto llegue el estado nuevo.
 *
 * No se puede leer al recibir el evento: los eventos llegan **antes** que el
 * estado que provocan, así que ahí el panel todavía es el de antes de descubrir
 * la letra y se anunciaría un panel viejo.
 */
let panelNeedsReading = false;

// --- Pintado ----------------------------------------------------------------

/** Repinta la pantalla entera con el último estado recibido. */
function render(): void {
  if (!state) return;
  roomLabel.textContent = state.roomCode;
  renderStatus();
  renderConfig();
  renderPlayers();
  renderPanel();
  renderActions();
  if (panelNeedsReading && state.round) {
    panelNeedsReading = false;
    // Sin cabecera: se acaba de decir la categoría al empezar la ronda y se
    // puede volver a pedir entera con Alt+P.
    announce(describePanel(state.round.panel, verbosity, false));
  }
  announceTurnChange();
}

/** Línea de estado: en qué punto está la partida. */
function renderStatus(): void {
  if (!state) return;
  const round = state.round;
  if (state.phase === 'lobby') {
    statusLine.textContent = `Vestíbulo. ${state.players.length} en la mesa. Comparte el código «${state.roomCode}» para que entre más gente.`;
  } else if (state.phase === 'gameOver') {
    statusLine.textContent = 'Partida terminada. El anfitrión puede empezar otra.';
  } else if (round) {
    // En la prueba final no hay número de ronda: `index` es null y escribirlo
    // tal cual dejaba un «Ronda null de 1» en pantalla.
    statusLine.textContent = round.isFinal
      ? `Prueba final. Se juega el bote: ${money(round.jackpot)}.`
      : `Ronda ${round.index} de ${state.config.rounds}. Bote: ${money(round.jackpot)}.`;
  }
  roundTools.hidden = state.phase !== 'playing';
  panelSection.hidden = !round;
  timeButton.hidden = !round?.deadline;
  syncClockWarnings(round?.deadline ?? null);
}

/**
 * Avisos del reloj de la prueba final.
 *
 * El tiempo no se puede ir leyendo solo (dejaría al lector de pantalla hablando
 * sin parar y taparía el juego), pero tampoco puede pasar en silencio: se avisa
 * en los dos momentos en los que cambia lo que uno haría, y el resto del tiempo
 * está el botón para preguntarlo.
 */
const CLOCK_WARNINGS_SECONDS = [30, 10];

/** Avisos programados del reloj, para poder cancelarlos si cambia la situación. */
let clockWarnings: number[] = [];
/** Fin de cuenta para el que ya están programados los avisos. */
let clockWarningsFor: number | null = null;

function syncClockWarnings(deadline: number | null): void {
  if (deadline === clockWarningsFor) return;
  for (const handle of clockWarnings) window.clearTimeout(handle);
  clockWarnings = [];
  clockWarningsFor = deadline;
  if (deadline === null) return;
  for (const seconds of CLOCK_WARNINGS_SECONDS) {
    const wait = deadline - seconds * 1000 - Date.now();
    if (wait <= 0) continue;
    clockWarnings.push(
      window.setTimeout(() => announce(`Quedan ${seconds} segundos.`), wait),
    );
  }
}

/** Configuración de la partida: mandos para el anfitrión, texto para el resto. */
function renderConfig(): void {
  if (!state) return;
  // Tocar una opción la manda al servidor y el estado que vuelve repinta esta
  // sección entera: los botones que había se tiran y el foco se cae al `body`.
  // De oído eso es lo peor que puede pasar, porque el lector no anuncia cómo ha
  // quedado la opción y hay que volver a bajar con el tabulador desde arriba en
  // cada cambio. Se apunta qué mando tenía el foco y se le devuelve al acabar:
  // al recuperarlo, el lector lo lee otra vez ya con su estado nuevo.
  const focused = configFocusId();
  configSection.replaceChildren();
  if (state.phase === 'playing') return;

  const title = document.createElement('h2');
  title.id = 'config-title';
  title.textContent = 'Cómo se juega esta partida';
  configSection.append(title);

  const config = state.config;
  if (state.hostId !== meId) {
    const summary = document.createElement('p');
    summary.textContent = describeConfig(config);
    configSection.append(summary);
    return;
  }

  configSection.append(
    numberRow('Rondas', config.rounds, (value) => setConfig({ rounds: value }), 1),
    choiceRow<GameConfig['finalMode']>(
      'Prueba final',
      [
        { id: 'noClock', label: 'Sin reloj' },
        { id: 'clock', label: `Con reloj (${config.finalSeconds} s)` },
      ],
      config.finalMode,
      (value) => setConfig({ finalMode: value }),
    ),
    choiceRow<GameConfig['wheelStop']>(
      'Parar la rueda',
      [
        { id: 'auto', label: 'Sola (azar puro)' },
        { id: 'manual', label: 'A mano (pulsando)' },
      ],
      config.wheelStop,
      (value) => setConfig({ wheelStop: value }),
    ),
    numberRow('Precio de la vocal', config.vowelPrice, (value) => setConfig({ vowelPrice: value }), 50),
    choiceRow<PanelLength>(
      'Longitud de las frases',
      [
        { id: 'short', label: 'Cortas' },
        { id: 'medium', label: 'Medias' },
        { id: 'long', label: 'Largas' },
      ],
      config.panelLength,
      (value) => setConfig({ panelLength: value }),
    ),
    packRow(config),
  );
  restoreConfigFocus(focused);
}

/** Identificador del mando de configuración que tiene el foco, si es alguno. */
function configFocusId(): string | null {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || !configSection.contains(active)) return null;
  return active.dataset.configId ?? null;
}

/** Devuelve el foco al mando que lo tenía antes de repintar, si sigue estando. */
function restoreConfigFocus(id: string | null): void {
  if (id === null) return;
  configSection.querySelector<HTMLElement>(`[data-config-id="${id}"]`)?.focus();
}

/**
 * Packs temáticos: se activan y desactivan uno a uno, así que van con
 * `aria-pressed` (marcable), no como opciones excluyentes.
 */
function packRow(config: GameConfig): HTMLElement {
  const row = document.createElement('div');
  row.className = 'config-row';
  row.setAttribute('role', 'group');
  row.setAttribute('aria-label', 'Packs temáticos');
  const caption = document.createElement('span');
  caption.className = 'config-legend';
  caption.textContent = 'Packs temáticos';
  row.append(caption);

  const available = state?.packs ?? [];
  if (available.length === 0) {
    const empty = document.createElement('span');
    empty.className = 'hint';
    empty.textContent = 'No hay packs instalados.';
    row.append(empty);
    return row;
  }

  for (const pack of available) {
    const active = config.packs.includes(pack.id);
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'secondary';
    toggle.textContent = pack.name;
    toggle.setAttribute('aria-pressed', String(active));
    // El número de frases va en la etiqueta hablada, no en el botón: en pantalla
    // sobra, pero de oído es lo que dice cuánto cambia la partida.
    toggle.setAttribute('aria-label', `${pack.name}, ${pack.phrases} frases`);
    toggle.dataset.configId = `pack:${pack.id}`;
    toggle.addEventListener('click', () => {
      const next = active
        ? config.packs.filter((id) => id !== pack.id)
        : [...config.packs, pack.id];
      setConfig({ packs: next });
    });
    row.append(toggle);
  }
  return row;
}

/** Configuración dicha en una frase, para quien no la puede tocar. */
function describeConfig(config: GameConfig): string {
  const final = config.finalMode === 'noClock' ? 'sin reloj' : `con reloj de ${config.finalSeconds} segundos`;
  const stop = config.wheelStop === 'auto' ? 'la rueda para sola' : 'la rueda se para a mano';
  const length = { short: 'cortas', medium: 'medias', long: 'largas' }[config.panelLength];
  const names = config.packs
    .map((id) => state?.packs.find((pack) => pack.id === id)?.name)
    .filter((name): name is string => Boolean(name));
  const packs = names.length > 0 ? ` Packs activos: ${names.join(', ')}.` : ' Sin packs temáticos.';
  return `${config.rounds} rondas, prueba final ${final}, ${stop}, la vocal cuesta ${money(config.vowelPrice)} y las frases son ${length}.${packs}`;
}

/** Fila de opciones excluyentes; el estado va en aria-pressed de TODOS los botones. */
function choiceRow<T extends string>(
  legend: string,
  options: readonly { id: T; label: string }[],
  current: T,
  onPick: (value: T) => void,
): HTMLElement {
  const row = document.createElement('div');
  row.className = 'config-row';
  row.setAttribute('role', 'group');
  row.setAttribute('aria-label', legend);
  const caption = document.createElement('span');
  caption.className = 'config-legend';
  caption.textContent = legend;
  row.append(caption);
  for (const option of options) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'secondary';
    button.textContent = option.label;
    // También en los no elegidos: marcando solo el activo, los demás ni siquiera
    // se anuncian como marcables y no se sabe que son opciones de lo mismo.
    button.setAttribute('aria-pressed', String(option.id === current));
    button.dataset.configId = `${legend}:${option.id}`;
    button.addEventListener('click', () => onPick(option.id));
    row.append(button);
  }
  return row;
}

/** Fila de un número con sus dos botones de ajuste. */
function numberRow(
  legend: string,
  value: number,
  onChange: (value: number) => void,
  step: number,
): HTMLElement {
  const row = document.createElement('div');
  row.className = 'config-row';
  row.setAttribute('role', 'group');
  row.setAttribute('aria-label', legend);
  const caption = document.createElement('span');
  caption.className = 'config-legend';
  caption.textContent = `${legend}: ${value}`;
  const less = document.createElement('button');
  less.type = 'button';
  less.className = 'secondary';
  less.textContent = `Menos ${legend.toLowerCase()}`;
  less.dataset.configId = `${legend}:menos`;
  // El número está en el rótulo de al lado, que el lector no vuelve a leer al
  // pulsar: si no va también en la etiqueta hablada del botón, quien escucha
  // ajusta a ciegas y no sabe en cuánto ha quedado.
  less.setAttribute('aria-label', `Menos ${legend.toLowerCase()}, ahora ${value}`);
  less.addEventListener('click', () => onChange(value - step));
  const more = document.createElement('button');
  more.type = 'button';
  more.className = 'secondary';
  more.textContent = `Más ${legend.toLowerCase()}`;
  more.dataset.configId = `${legend}:mas`;
  more.setAttribute('aria-label', `Más ${legend.toLowerCase()}, ahora ${value}`);
  more.addEventListener('click', () => onChange(value + step));
  row.append(caption, less, more);
  return row;
}

/** Lista de jugadores con su dinero y lo que llevan encima. */
function renderPlayers(): void {
  if (!state) return;
  playersList.replaceChildren();
  for (const player of state.players) {
    const item = document.createElement('li');
    item.className = 'player';
    if (state.round?.turnPlayerId === player.id) item.classList.add('current');

    const name = document.createElement('span');
    name.className = 'name';
    name.textContent = player.id === meId ? `${player.name} (tú)` : player.name;

    const meta = document.createElement('span');
    meta.className = 'meta';
    meta.textContent = playerMeta(player, state.round);

    item.append(name, meta);
    if (player.isBot && (state.phase === 'lobby' || state.phase === 'gameOver') && state.hostId === meId) {
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'secondary';
      remove.textContent = `Quitar a ${player.name}`;
      remove.addEventListener('click', () => send({ type: 'removeBot', playerId: player.id }));
      item.append(remove);
    }
    playersList.append(item);
  }
}

/** Todo lo que hay que saber de un jugador, escrito (nunca solo por color). */
function playerMeta(player: PlayerView, round: RoundView | null): string {
  const parts: string[] = [];
  if (player.isBot) parts.push(`bot de dificultad ${difficultyLabel(player.difficulty)}`);
  if (!player.connected) parts.push('desconectado');
  if (round) {
    parts.push(`${money(player.bank)} en el banco`);
    parts.push(`${money(player.pot)} en esta ronda`);
    if (player.wildcard) parts.push('con comodín');
    if (player.surprise) parts.push('con sobre');
    if (player.doubler) parts.push('con el por dos');
    if (player.jackpot) parts.push('en juego por el bote');
    if (round.turnPlayerId === player.id) parts.push('le toca');
  }
  return parts.join(' · ');
}

/** Nombre de una dificultad tal y como se le dice al jugador. */
function difficultyLabel(difficulty: BotDifficulty | undefined): string {
  return BOT_DIFFICULTIES.find((entry) => entry.id === difficulty)?.label.toLowerCase() ?? 'media';
}

/**
 * Pinta el panel en sus dos formas: la dibujada (solo para quien mira) y la
 * lista de palabras que recorre el lector de pantalla.
 */
function renderPanel(): void {
  const round = state?.round;
  if (!round) return;
  panelCategory.textContent = `Categoría: ${round.panel.category}`;

  panelVisual.replaceChildren();
  for (const word of round.panel.words) {
    const wordBox = document.createElement('span');
    wordBox.className = 'panel-word';
    for (const cell of word) {
      const cellBox = document.createElement('span');
      cellBox.className = cell === '' ? 'cell empty' : 'cell';
      cellBox.textContent = cell === '' ? ' ' : cell;
      wordBox.append(cellBox);
    }
    panelVisual.append(wordBox);
  }

  // Dentro de una ronda la forma no cambia, solo se destapan casillas. Rehacer
  // la lista ahí es lo peor que se puede hacer de oído: el lector recorre estos
  // elementos con su cursor virtual, y a diferencia del foco ese cursor no se
  // puede guardar y devolver desde el código. Si el nodo donde estaba el jugador
  // desaparece, pierde el sitio y tiene que volver a bajar desde arriba, y eso
  // pasa cada vez que acierta CUALQUIERA de la mesa. Así que solo se rehace
  // cuando de verdad cambia la forma; el resto de las veces se escribe encima.
  const shape = panelShapeKey(round.panel.words);
  if (shape === panelShape) {
    updatePanelWords(round.panel.words);
    return;
  }
  panelShape = shape;
  buildPanelWords(round.panel.words);
}

/**
 * @brief Resume la forma de la lista: si cambia, hay que rehacerla.
 *
 * La verbosidad entra porque cambia la estructura entera (con casillas o sin
 * ellas), no solo el texto.
 *
 * @param words Palabras del panel.
 * @return Firma comparable con la del pintado anterior.
 */
function panelShapeKey(words: readonly (readonly string[])[]): string {
  return `${verbosity}|${words.map((word) => word.length).join('-')}`;
}

/**
 * @brief Monta la lista del panel desde cero.
 *
 * Agrupando, una palabra por elemento y su lectura entera. Casilla a casilla, la
 * palabra pasa a ser el elemento que agrupa y cada casilla es un elemento
 * propio: es lo único que deja pararse en la posición 3 con las flechas, que es
 * lo que promete el modo explorar.
 *
 * @param words Palabras del panel.
 */
function buildPanelWords(words: readonly (readonly string[])[]): void {
  panelWords.replaceChildren();
  words.forEach((word, index) => {
    const item = document.createElement('li');

    if (verbosity === 'grouped') {
      item.textContent = describeWord(word, index, verbosity);
      panelWords.append(item);
      return;
    }

    item.textContent = describeWordHeader(word, index);
    const cellList = document.createElement('ul');
    cellList.className = 'panel-cells';
    // `role="list"` explícito: quitar los topos con `list-style: none` hace que
    // algunos lectores dejen de tratarlo como lista, y aquí la lista es
    // justamente lo que dice que estas casillas son de esta palabra.
    cellList.setAttribute('role', 'list');
    word.forEach((cell, position) => {
      const cellItem = document.createElement('li');
      cellItem.textContent = describeCell(cell, position);
      cellList.append(cellItem);
    });
    item.append(cellList);
    panelWords.append(item);
  });
}

/**
 * @brief Pone al día los textos de la lista sin tocar ni un nodo.
 *
 * Solo se llama cuando la forma coincide con la del último montaje, así que los
 * elementos que hacen falta están todos donde se espera.
 *
 * @param words Palabras del panel, ya con las letras descubiertas.
 */
function updatePanelWords(words: readonly (readonly string[])[]): void {
  words.forEach((word, index) => {
    const item = panelWords.children[index];

    if (verbosity === 'grouped') {
      writeTextInPlace(item, describeWord(word, index, verbosity));
      return;
    }

    writeTextInPlace(item, describeWordHeader(word, index));
    const cells = item.querySelectorAll('li');
    word.forEach((cell, position) => writeTextInPlace(cells[position], describeCell(cell, position)));
  });
}

/**
 * @brief Cambia el texto de un elemento escribiendo encima del nodo que ya hay.
 *
 * `elemento.textContent = …` **no** vale aquí: tira el nodo de texto y mete
 * otro, o sea otra mutación de estructura, que es justo lo que estamos
 * evitando. Escribiendo en `data` la mutación es solo de datos y el lector
 * conserva el sitio.
 *
 * @param element Elemento cuyo primer hijo es el nodo de texto.
 * @param text Texto nuevo.
 */
function writeTextInPlace(element: Element, text: string): void {
  const first = element.firstChild;
  if (first instanceof Text) {
    // Comparar antes de escribir evita mutaciones que no cambian nada, que
    // algunos lectores sí acusan.
    if (first.data !== text) first.data = text;
    return;
  }
  element.prepend(document.createTextNode(text));
}

// --- Mandos -----------------------------------------------------------------

/**
 * Pinta los mandos y se ocupa del foco, que el repintado se lleva por delante.
 *
 * Cada jugada cambia la botonera entera (tiras y salen las consonantes, pides
 * letra y vuelven las tres acciones), así que el botón que acabas de pulsar deja
 * de existir y el foco se cae al `body`: de oído hay que volver a bajar desde el
 * principio de la página **en cada jugada**. Se le devuelve al mismo mando si
 * sigue estando y, si no, a la caja de mandos, que se anuncia y deja al jugador
 * dentro de lo que le toca hacer ahora.
 */
function renderActions(): void {
  if (!state) return;
  const enfocado = actionFocusId();
  actionsBox.replaceChildren();
  paintActions();
  restoreActionFocus(enfocado);
}

/**
 * Identificador del mando que tenía el foco.
 *
 * `null` si el jugador **no** estaba en los mandos: puede estar leyendo el panel
 * con las flechas, y ahí moverle el foco es arrancarle de donde estaba. Cadena
 * vacía si estaba en la caja pero en algo sin identificador.
 */
function actionFocusId(): string | null {
  const activo = document.activeElement;
  if (!(activo instanceof HTMLElement) || !actionsBox.contains(activo)) return null;
  return activo.dataset.actionId ?? '';
}

/** Devuelve el foco a los mandos si el jugador ya lo tenía ahí. */
function restoreActionFocus(previo: string | null): void {
  if (previo === null) return;
  // Si el propio pintado ya ha movido el foco (parar la rueda, escribir la
  // solución), manda él: son los dos momentos en los que el juego decide.
  if (document.activeElement instanceof HTMLElement && actionsBox.contains(document.activeElement)) return;
  const mismo = previo ? actionsBox.querySelector<HTMLElement>(`[data-action-id="${previo}"]`) : null;
  (mismo ?? actionsBox).focus();
}

/** Elige y pinta los mandos que correspondan al momento de la partida. */
function paintActions(): void {
  if (!state) return;
  if (state.phase !== 'playing') {
    renderLobbyActions();
    return;
  }
  const round = state.round;
  if (!round) return;

  if (round.turnPlayerId !== meId) {
    actionsBox.append(note(describeTurn(round, state.players, meId)));
    return;
  }
  if (pending === 'vowel') {
    renderVowelChoice(false);
    return;
  }
  if (pending === 'solve') {
    renderSolveForm();
    return;
  }

  switch (round.turn.kind) {
    case 'spinning': {
      actionsBox.append(note('La rueda está girando. Púlsalo cuando quieras pararla.'));
      const stop = button('¡Parar la rueda!', () => send({ type: 'stopWheel' }));
      actionsBox.append(stop);
      // El motivo, escrito: si no se pulsa no pasa nada malo, y saberlo quita la
      // presión de tener que reaccionar rápido.
      actionsBox.append(note('Si no la paras, se para sola.'));
      // Aquí sí se mueve el foco, y es lo correcto: la rueda dura unos segundos
      // y llegar al botón con el tabulador se comería el tiempo entero. Es la
      // única acción posible en ese momento, así que no se pierde nada.
      stop.focus();
      break;
    }
    case 'consonant':
      actionsBox.append(note(`Pide una consonante. Cada aparición vale ${money(round.turn.value)}.`));
      renderConsonantChoice(round);
      break;
    case 'express':
      actionsBox.append(
        note(`Exprés: cada consonante vale ${money(round.turn.value)}, pero si fallas una, quiebras.`),
      );
      renderConsonantChoice(round);
      actionsBox.append(
        button('Plantarme y dejar el exprés', () => send({ type: 'stopExpress' }), 'plantarse'),
      );
      break;
    case 'freeVowel':
      actionsBox.append(note('Vocal gratis: elige una y no pagas nada.'));
      renderVowelChoice(true);
      break;
    case 'penalty':
      actionsBox.append(note(`Ha salido ${round.turn.label}. Puedes gastar el comodín para librarte.`));
      actionsBox.append(
        button('Gastar el comodín y librarme', () => send({ type: 'wildcardSave', spend: true }), 'comodin-gastar'),
        button('Guardar el comodín y aguantar', () => send({ type: 'wildcardSave', spend: false }), 'comodin-guardar'),
      );
      break;
    case 'finalLetters':
      actionsBox.append(
        note(
          'Elige tus letras. No se descubren hasta que las tengas todas, así que no puedes ir viendo lo que sale.',
        ),
      );
      if (round.turn.consonants > 0) renderConsonantChoice(round);
      else renderVowelChoice(true);
      break;
    case 'finalSolve':
      actionsBox.append(
        note(
          round.turn.attemptsLeft === null
            ? 'Di la frase antes de que se acabe el tiempo.'
            : round.turn.attemptsLeft === 1
              ? 'Te queda un intento.'
              : `Te quedan ${round.turn.attemptsLeft} intentos.`,
        ),
      );
      renderSolveForm(false);
      break;
    case 'choosing':
    default:
      renderMainChoices(round);
      break;
  }
}

/** Mandos del vestíbulo: bots y arranque. */
function renderLobbyActions(): void {
  if (!state) return;
  if (state.hostId !== meId) {
    actionsBox.append(note('Esperando a que el anfitrión empiece la partida.'));
    return;
  }
  const bots = document.createElement('div');
  bots.className = 'action-row';
  bots.setAttribute('role', 'group');
  bots.setAttribute('aria-label', 'Añadir bots');
  for (const difficulty of BOT_DIFFICULTIES) {
    bots.append(
      button(
        `Añadir bot de dificultad ${difficulty.label.toLowerCase()}`,
        () => send({ type: 'addBot', difficulty: difficulty.id }),
        `bot-${difficulty.id}`,
      ),
    );
  }
  actionsBox.append(bots);
  if (state.players.length < 2) {
    actionsBox.append(note('Hacen falta al menos dos jugadores. Añade un bot para completar la mesa.'));
  } else {
    actionsBox.append(button('Empezar partida', () => send({ type: 'start' }), 'empezar'));
  }
}

/** Las tres acciones de un turno, más el comodín si lo hay. */
function renderMainChoices(round: RoundView): void {
  const me = state?.players.find((player) => player.id === meId);
  const price = state?.config.vowelPrice ?? 0;
  const row = document.createElement('div');
  row.className = 'action-row';
  row.setAttribute('role', 'group');
  row.setAttribute('aria-label', 'Tu turno');

  if (round.panel.words.flat().some((cell) => cell === '')) {
    row.append(button('Tirar la rueda', () => send({ type: 'spin' }), 'tirar'));
  }
  const pot = me?.pot ?? 0;
  if (pot >= price) {
    row.append(
      button(
        `Comprar una vocal (${money(price)})`,
        () => {
          pending = 'vowel';
          renderActions();
        },
        'comprar-vocal',
      ),
    );
  }
  if (me?.wildcard) {
    row.append(
      button('Gastar el comodín en una consonante', () => send({ type: 'wildcardConsonant' }), 'comodin-consonante'),
    );
  }
  row.append(
    button(
      'Resolver el panel',
      () => {
        pending = 'solve';
        renderActions();
      },
      'resolver',
    ),
  );
  actionsBox.append(row);

  // El motivo, escrito y a la vista: un botón apagado no lo explicaría.
  if (pot < price) {
    actionsBox.append(
      note(`Todavía no puedes comprar vocal: cuesta ${money(price)} y llevas ${money(pot)} en esta ronda.`),
    );
  }
}

/** Botonera de consonantes, sin las ya pedidas ni las ya elegidas en la final. */
function renderConsonantChoice(round: RoundView): void {
  const used = new Set([...round.panel.used, ...round.finalPicks]);
  const grid = document.createElement('div');
  grid.className = 'letters';
  grid.setAttribute('role', 'group');
  grid.setAttribute('aria-label', 'Consonantes');
  for (const letter of CONSONANTS) {
    if (used.has(letter)) continue;
    grid.append(letterButton(letter, `Pedir la ${letter}`, () => send({ type: 'consonant', letter })));
  }
  actionsBox.append(grid);
}

/**
 * Botonera de vocales.
 * @param free true si la vocal viene de la casilla y no se paga.
 */
function renderVowelChoice(free: boolean): void {
  const round = state?.round;
  if (!round) return;
  const used = new Set([...round.panel.used, ...round.finalPicks]);
  const price = state?.config.vowelPrice ?? 0;
  if (!free) {
    actionsBox.append(
      note(`Elige la vocal. Cuesta ${money(price)} y se paga salga o no; si no está, pierdes el turno.`),
    );
  }
  const grid = document.createElement('div');
  grid.className = 'letters';
  grid.setAttribute('role', 'group');
  grid.setAttribute('aria-label', 'Vocales');
  for (const letter of VOWELS) {
    if (used.has(letter)) continue;
    const label = free ? `Elegir la ${letter}` : `Comprar la ${letter}`;
    grid.append(
      letterButton(letter, label, () => {
        pending = 'none';
        send({ type: 'vowel', letter });
      }),
    );
  }
  actionsBox.append(grid);
  if (!free) actionsBox.append(button('Mejor no, volver', cancelPending, 'volver'));
}

/**
 * Campo para decir la frase.
 *
 * @param cancellable true si se puede volver atrás. En la prueba final no: ahí
 *        resolver no es una opción entre otras, es lo único que queda por hacer.
 */
function renderSolveForm(cancellable = true): void {
  const form = document.createElement('form');
  form.className = 'play-form';
  const field = document.createElement('div');
  field.className = 'field';
  const label = document.createElement('label');
  label.setAttribute('for', 'solve-input');
  label.textContent = 'Escribe la frase entera';
  const input = document.createElement('input');
  input.id = 'solve-input';
  input.autocomplete = 'off';
  input.maxLength = 120;
  field.append(label, input);
  const submit = document.createElement('button');
  submit.type = 'submit';
  submit.textContent = 'Resolver';
  form.append(field, submit);
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const text = input.value.trim();
    if (!text) return;
    pending = 'none';
    send({ type: 'solve', text });
  });
  actionsBox.append(form, note('Da igual la tilde, el signo o la mayúscula.'));
  if (cancellable) actionsBox.append(button('Mejor no, volver', cancelPending, 'volver'));
  input.focus();
}

/** Vuelve de un paso intermedio (elegir vocal, escribir la solución). */
function cancelPending(): void {
  pending = 'none';
  // El foco lo devuelve `renderActions`: venías de «Mejor no, volver», que ya no
  // existe, así que aterrizas en la caja de mandos con las tres acciones.
  renderActions();
}

/**
 * Botón de acción normal.
 *
 * @param actionId Identificador estable del mando, para devolverle el foco si
 *        sobrevive al repintado. Se omite en lo que no lo necesita.
 */
function button(label: string, onClick: () => void, actionId?: string): HTMLButtonElement {
  const element = document.createElement('button');
  element.type = 'button';
  element.textContent = label;
  if (actionId) element.dataset.actionId = actionId;
  element.addEventListener('click', onClick);
  return element;
}

/**
 * Botón de una letra. Se ve la letra sola, pero se anuncia la acción entera:
 * una botonera de veintidós botones llamados «B», «C», «D»… no dice qué hacen.
 */
function letterButton(letter: Letter, label: string, onClick: () => void): HTMLButtonElement {
  const element = document.createElement('button');
  element.type = 'button';
  element.className = 'letter';
  element.textContent = letter;
  element.setAttribute('aria-label', label);
  // La letra vale de identificador: en un momento dado solo hay una botonera, la
  // de consonantes o la de vocales, así que no se pisan.
  element.dataset.actionId = `letra-${letter}`;
  element.addEventListener('click', onClick);
  return element;
}

/** Texto explicativo dentro de la zona de mandos. */
function note(text: string): HTMLParagraphElement {
  const element = document.createElement('p');
  element.className = 'hint';
  element.textContent = text;
  return element;
}

// --- Envíos -----------------------------------------------------------------

/** Manda una intención al servidor y avisa si no hay conexión. */
function send(message: Parameters<Net['send']>[0]): void {
  if (!net.send(message)) announce('No hay conexión con el servidor. Reintentando…');
}

/** Cambia la configuración (solo tiene efecto si eres el anfitrión). */
function setConfig(patch: Partial<GameConfig>): void {
  send({ type: 'setConfig', config: patch });
}

/** Anuncia el turno cuando cambia de dueño o de qué se espera. */
function announceTurnChange(): void {
  const round = state?.round;
  if (!round || !state) {
    announcedTurn = '';
    return;
  }
  const key = `${round.turnPlayerId}|${round.turn.kind}|${'value' in round.turn ? round.turn.value : ''}`;
  if (key === announcedTurn) return;
  announcedTurn = key;
  // El cambio de jugador ya lo cuenta el evento `turnStarted`; lo que hace falta
  // añadir es qué se espera ahora, que es lo que cambia los mandos.
  if (round.turnPlayerId === meId) announce(describeTurn(round, state.players, meId));
}

// --- Consultas y atajos -----------------------------------------------------

/** Responde a una consulta, o explica que todavía no hay partida. */
function consult(answer: () => string): void {
  reply(state?.round ? answer() : 'Todavía no hay ninguna ronda en marcha.');
}

const CONSULTS: Record<string, () => void> = {
  KeyP: () => consult(() => describePanel(state!.round!.panel, verbosity)),
  KeyL: () => consult(() => describeUsedLetters(state!.round!.panel.used)),
  KeyD: () => reply(state ? describeMoney(state.players, meId) : 'Todavía no hay jugadores.'),
  KeyT: () => consult(() => describeTurn(state!.round!, state!.players, meId)),
  KeyB: () => consult(() => `El bote está en ${money(state!.round!.jackpot)}.`),
};

byId('btn-panel').addEventListener('click', () => CONSULTS.KeyP());
byId('btn-letters').addEventListener('click', () => CONSULTS.KeyL());
byId('btn-money').addEventListener('click', () => CONSULTS.KeyD());
byId('btn-turn').addEventListener('click', () => CONSULTS.KeyT());
byId('btn-jackpot').addEventListener('click', () => CONSULTS.KeyB());
timeButton.addEventListener('click', () => reply(describeTimeLeft(state?.round?.deadline ?? null)));

document.addEventListener('keydown', (event) => {
  if (!event.altKey || event.ctrlKey || event.metaKey) return;
  const consultation = CONSULTS[event.code];
  if (consultation) {
    event.preventDefault();
    consultation();
    return;
  }
  const index = historyIndexFromKey(event.key, event.code);
  if (index !== null) {
    event.preventDefault();
    const item = history.recall(index);
    historyRegion.textContent = item.text;
  }
});

// --- Opciones ---------------------------------------------------------------

/** Pone al día el botón de silencio. */
function refreshMuteButton(): void {
  muteButton.textContent = sound.isMuted ? 'Activar los sonidos' : 'Silenciar los sonidos';
}

muteButton.addEventListener('click', () => {
  const muted = sound.toggleMuted();
  refreshMuteButton();
  reply(muted ? 'Sonidos silenciados.' : 'Sonidos activados.');
});

/**
 * El navegador no deja sonar nada hasta que la persona ha tocado la página, así
 * que el audio se prepara en el primer gesto, sea del ratón o del teclado.
 */
for (const gesture of ['click', 'keydown'] as const) {
  document.addEventListener(gesture, () => sound.unlock(), { once: true });
}

/** Pone al día el botón que cambia la forma de leer el panel. */
function refreshVerbosityButton(): void {
  verbosityButton.textContent =
    verbosity === 'grouped' ? 'Leer casilla a casilla' : 'Leer agrupando los huecos';
  verbosityButton.setAttribute(
    'aria-label',
    verbosity === 'grouped'
      ? 'Leer el panel casilla a casilla. Ahora se lee agrupando los huecos.'
      : 'Leer el panel agrupando los huecos. Ahora se lee casilla a casilla.',
  );
}

verbosityButton.addEventListener('click', () => {
  verbosity = verbosity === 'grouped' ? 'cell' : 'grouped';
  localStorage.setItem(VERBOSITY_KEY, verbosity);
  refreshVerbosityButton();
  renderPanel();
  reply(
    verbosity === 'grouped'
      ? 'El panel se leerá agrupando los huecos.'
      : 'El panel se leerá casilla a casilla.',
  );
});

/** Pone al día los dos botones de tema (hay uno en cada pantalla). */
function refreshThemeButtons(): void {
  for (const id of ['btn-theme-join', 'btn-theme-game']) {
    byId(id).textContent = `Tema: ${theme.label}`;
  }
}

for (const id of ['btn-theme-join', 'btn-theme-game']) {
  byId(id).addEventListener('click', () => {
    theme.next();
    refreshThemeButtons();
    reply(`Tema ${theme.label}.`);
  });
}

for (const id of ['btn-help-join', 'btn-help-game']) {
  byId(id).addEventListener('click', (event) => help.show(event.currentTarget as HTMLElement));
}

// --- Entrada en la sala -----------------------------------------------------

joinForm.addEventListener('submit', (event) => {
  event.preventDefault();
  const room = roomInput.value.trim().toUpperCase();
  const name = nameInput.value.trim();
  if (!room || !name) {
    joinError.textContent = 'Escribe el código de sala y tu nombre.';
    return;
  }
  if (!net.send({ type: 'join', roomCode: room, name })) {
    joinError.textContent = 'Todavía no hay conexión con el servidor. Espera un momento y vuelve a intentarlo.';
  }
});

// --- Arranque ---------------------------------------------------------------

refreshThemeButtons();
refreshVerbosityButton();
refreshMuteButton();
history.record(plain('Bienvenido a la ruleta accesible.').text);
net.connect();
